import React, { useState, useEffect, useRef, useCallback } from 'react';
import './AgendamentosCliente.css';
import EstadoCarregando from './EstadoCarregando';
import EstadoVazio from './EstadoVazio';
import EstadoErro from './EstadoErro';
import { apiFetch, ApiError } from './api';

/**
 * AgendamentosCliente — agenda de ações de um cliente (reuniões, ligações,
 * e-mails, visitas). Lista/filtra por status, cria, conclui e cancela
 * agendamentos.
 *
 * Spec: SEF-15#document-spec-ux. Consome a API real (SEF-25 / PRD-17):
 * GET/POST /api/clientes/{id}/agendamentos e PATCH .../{id}/status. Estados de
 * carregando/vazio/erro usam os componentes compartilhados de SEF-14.
 */

const ROTULOS_TIPO = {
  reuniao: 'Reunião',
  ligacao: 'Ligação',
  email: 'E-mail',
  visita: 'Visita',
  outro: 'Outro',
};

const ROTULOS_STATUS = {
  pendente: 'Pendente',
  concluido: 'Concluído',
  cancelado: 'Cancelado',
};

// O backend devolve `status` como label ('Pendente'/...); a UI agrupa por token.
const STATUS_LABEL_PARA_TOKEN = {
  Pendente: 'pendente',
  'Concluído': 'concluido',
  Cancelado: 'cancelado',
};

function normalizarAgendamento(a) {
  return { ...a, status: STATUS_LABEL_PARA_TOKEN[a.status] || a.status };
}

const MESES_ABREV = [
  'JAN', 'FEV', 'MAR', 'ABR', 'MAI', 'JUN',
  'JUL', 'AGO', 'SET', 'OUT', 'NOV', 'DEZ',
];

const FILTROS = [
  { chave: 'todos', rotulo: 'Todos' },
  { chave: 'pendente', rotulo: 'Pendentes' },
  { chave: 'concluido', rotulo: 'Concluídos' },
  { chave: 'cancelado', rotulo: 'Cancelados' },
];

const ORDEM_GRUPOS = ['pendente', 'concluido', 'cancelado'];

function hojeISO() {
  return new Date().toISOString().slice(0, 10);
}

function formularioVazio() {
  return {
    data: hojeISO(),
    hora: '09:00',
    tipo: 'reuniao',
    assunto: '',
    descricao: '',
  };
}

// --- Ordenação ------------------------------------------------------------
function compararCronologico(a, b, asc) {
  const x = `${a.data} ${a.hora}`;
  const y = `${b.data} ${b.hora}`;
  if (x === y) return 0;
  return asc ? (x < y ? -1 : 1) : (x < y ? 1 : -1);
}

function ordenarPorGrupo(lista, status) {
  // Pendentes: próximos primeiro (asc). Concluídos/cancelados: mais recentes primeiro (desc).
  const asc = status === 'pendente';
  return [...lista].sort((a, b) => compararCronologico(a, b, asc));
}

// --- Componente -----------------------------------------------------------
function AgendamentosCliente({ clienteId, onNavegar }) {
  const [estado, setEstado] = useState('carregando'); // carregando | ok | erro | naoEncontrado
  const [cliente, setCliente] = useState(null);
  const [agendamentos, setAgendamentos] = useState([]);
  const [filtro, setFiltro] = useState('todos');

  const [modalAberto, setModalAberto] = useState(false);
  const [form, setForm] = useState(formularioVazio());
  const [errosForm, setErrosForm] = useState({});
  const [salvando, setSalvando] = useState(false);
  const [erroSalvar, setErroSalvar] = useState(false);

  const [confirmacao, setConfirmacao] = useState(null); // { id, assunto }
  const [toasts, setToasts] = useState([]); // { id, tipo: 'sucesso'|'erro', texto }
  const [destaqueId, setDestaqueId] = useState(null);

  const botaoAbriuModalRef = useRef(null);
  const primeiroCampoRef = useRef(null);
  const modalRef = useRef(null);
  const confirmRef = useRef(null);

  const carregar = useCallback(() => {
    setEstado('carregando');
    Promise.all([
      apiFetch(`/api/clientes/${clienteId}`),
      apiFetch(`/api/clientes/${clienteId}/agendamentos`),
    ])
      .then(([cli, lista]) => {
        setCliente(cli);
        setAgendamentos((lista?.agendamentos || []).map(normalizarAgendamento));
        setEstado('ok');
      })
      .catch((e) => {
        if (e instanceof ApiError && e.status === 404) setEstado('naoEncontrado');
        else setEstado('erro');
      });
  }, [clienteId]);

  useEffect(() => { carregar(); }, [carregar]);

  // --- Toasts ---
  const adicionarToast = useCallback((tipo, texto) => {
    const id = Date.now() + Math.random();
    setToasts((t) => [...t, { id, tipo, texto }]);
    setTimeout(() => {
      setToasts((t) => t.filter((x) => x.id !== id));
    }, 4000);
  }, []);

  // --- Navegação de volta ---
  const voltarParaCliente = () => {
    if (cliente) onNavegar('detalhes-cliente', { clienteId: cliente.id });
    else onNavegar('clientes');
  };

  // --- Trap de foco genérico para diálogos ---
  function aoTeclarDialog(e, refEl, fechar) {
    if (e.key === 'Escape') {
      e.stopPropagation();
      fechar();
      return;
    }
    if (e.key !== 'Tab' || !refEl.current) return;
    const focaveis = refEl.current.querySelectorAll(
      'a[href], button:not([disabled]), textarea:not([disabled]), input:not([disabled]), select:not([disabled]), [tabindex]:not([tabindex="-1"])'
    );
    if (focaveis.length === 0) return;
    const primeiro = focaveis[0];
    const ultimo = focaveis[focaveis.length - 1];
    if (e.shiftKey && document.activeElement === primeiro) {
      e.preventDefault();
      ultimo.focus();
    } else if (!e.shiftKey && document.activeElement === ultimo) {
      e.preventDefault();
      primeiro.focus();
    }
  }

  // --- Modal de novo agendamento ---
  const abrirModal = (e) => {
    botaoAbriuModalRef.current = (e && e.currentTarget) || null;
    setForm(formularioVazio());
    setErrosForm({});
    setErroSalvar(false);
    setModalAberto(true);
  };

  const fecharModal = useCallback(() => {
    setModalAberto(false);
    setSalvando(false);
    if (botaoAbriuModalRef.current) botaoAbriuModalRef.current.focus();
  }, []);

  useEffect(() => {
    if (modalAberto && primeiroCampoRef.current) primeiroCampoRef.current.focus();
  }, [modalAberto]);

  const aoMudarCampo = (e) => {
    const { name, value } = e.target;
    setForm((f) => ({ ...f, [name]: value }));
    setErrosForm((er) => ({ ...er, [name]: undefined }));
  };

  function validarForm(f) {
    const er = {};
    if (!f.data) er.data = 'Informe a data.';
    if (!f.hora) er.hora = 'Informe a hora.';
    if (!f.tipo) er.tipo = 'Informe o tipo de ação.';
    if (!f.assunto.trim()) er.assunto = 'Informe o assunto.';
    return er;
  }

  const dataNoPassado = form.data && form.data < hojeISO();

  const salvarAgendamento = (e) => {
    e.preventDefault();
    const er = validarForm(form);
    setErrosForm(er);
    if (Object.keys(er).length > 0) return;

    setSalvando(true);
    setErroSalvar(false);
    // responsavelId não é enviado: o backend assume o usuário logado. O campo
    // "responsável" da UI ainda é texto livre — vincular a um usuário real
    // (picker + responsavelId) é follow-up (precisa do endpoint de usuários).
    apiFetch(`/api/clientes/${clienteId}/agendamentos`, {
      method: 'POST',
      body: {
        data: form.data,
        hora: form.hora,
        tipo: form.tipo,
        assunto: form.assunto.trim(),
        descricao: form.descricao.trim(),
      },
    })
      .then((criado) => {
        const novo = normalizarAgendamento(criado);
        setAgendamentos((lista) => [...lista, novo]);
        setDestaqueId(novo.id);
        setTimeout(() => setDestaqueId(null), 2200);
        setSalvando(false);
        setModalAberto(false);
        if (botaoAbriuModalRef.current) botaoAbriuModalRef.current.focus();
        adicionarToast('sucesso', 'Agendamento criado.');
      })
      .catch(() => {
        setSalvando(false);
        setErroSalvar(true);
      });
  };

  // --- Concluir / cancelar (otimista com rollback) ---
  const atualizarStatus = (id, novoStatus) => {
    const anterior = agendamentos;
    setAgendamentos((lista) => lista.map((a) => (a.id === id ? { ...a, status: novoStatus } : a)));
    apiFetch(`/api/clientes/${clienteId}/agendamentos/${id}/status`, {
      method: 'PATCH',
      body: { novoStatus },
    })
      .then(() => {
        adicionarToast(
          'sucesso',
          novoStatus === 'concluido' ? 'Agendamento concluído.' : 'Agendamento cancelado.');
      })
      .catch(() => {
        setAgendamentos(anterior);
        adicionarToast('erro', 'Não foi possível atualizar o agendamento.');
      });
  };

  const concluir = (ag) => atualizarStatus(ag.id, 'concluido');

  const pedirCancelamento = (ag) => setConfirmacao({ id: ag.id, assunto: ag.assunto });
  const confirmarCancelamento = () => {
    if (confirmacao) atualizarStatus(confirmacao.id, 'cancelado');
    setConfirmacao(null);
  };

  // --- Derivados de render ---
  const contagens = agendamentos.reduce(
    (acc, a) => { acc[a.status] = (acc[a.status] || 0) + 1; acc.todos += 1; return acc; },
    { todos: 0, pendente: 0, concluido: 0, cancelado: 0 }
  );

  const listaFiltrada = filtro === 'todos' ? agendamentos : agendamentos.filter((a) => a.status === filtro);

  // --- Sub-render: cartão ---
  function Cartao({ ag, destaque }) {
    const partes = ag.data.split('-').map(Number);
    const ano = partes[0];
    const mes = partes[1];
    const dia = partes[2];
    const ehPendente = ag.status === 'pendente';
    return (
      <li className={`ac-cartao ac-cartao--${ag.status}${destaque ? ' ac-cartao--destaque' : ''}`}>
        <div className="ac-cartao__data" aria-hidden="true">
          <span className="ac-cartao__dia">{String(dia).padStart(2, '0')}</span>
          <span className="ac-cartao__mes">{MESES_ABREV[(mes - 1) % 12]}</span>
          <span className="ac-cartao__ano">{ano}</span>
        </div>
        <div className="ac-cartao__info">
          <div className="ac-cartao__horario">
            {ag.hora} · {ROTULOS_TIPO[ag.tipo] || ag.tipo}
          </div>
          <div className="ac-cartao__assunto">{ag.assunto}</div>
          <div className="ac-cartao__responsavel">Responsável: {ag.responsavel}</div>
        </div>
        <div className="ac-cartao__lateral">
          <span className={`ac-badge ac-badge--${ag.status}`}>{ROTULOS_STATUS[ag.status]}</span>
          {ehPendente && (
            <div className="ac-cartao__acoes">
              <button
                type="button"
                className="ac-btn-acao ac-btn-acao--concluir"
                onClick={() => concluir(ag)}
                aria-label={`Concluir agendamento "${ag.assunto}"`}
              >
                ✓ Concluir
              </button>
              <button
                type="button"
                className="ac-btn-acao ac-btn-acao--cancelar"
                onClick={() => pedirCancelamento(ag)}
                aria-label={`Cancelar agendamento "${ag.assunto}"`}
              >
                ✕ Cancelar
              </button>
            </div>
          )}
        </div>
      </li>
    );
  }

  // --- Sub-render: lista (agrupada ou plana) ---
  function renderLista() {
    if (listaFiltrada.length === 0) {
      const rotulo = ROTULOS_STATUS[filtro] ? ROTULOS_STATUS[filtro].toLowerCase() : '';
      return (
        <EstadoVazio
          icone={null}
          titulo={`Nenhum agendamento ${rotulo}.`}
          acao={<button type="button" className="ac-link" onClick={() => setFiltro('todos')}>Ver todos</button>}
        />
      );
    }

    if (filtro === 'todos') {
      return ORDEM_GRUPOS.map((status) => {
        const doGrupo = ordenarPorGrupo(agendamentos.filter((a) => a.status === status), status);
        if (doGrupo.length === 0) return null;
        return (
          <section key={status} className="ac-grupo" aria-label={ROTULOS_STATUS[status]}>
            <h2 className="ac-grupo__titulo">{ROTULOS_STATUS[status].toUpperCase()}S</h2>
            <ul className="ac-lista">
              {doGrupo.map((ag) => <Cartao key={ag.id} ag={ag} destaque={ag.id === destaqueId} />)}
            </ul>
          </section>
        );
      });
    }

    return (
      <ul className="ac-lista">
        {ordenarPorGrupo(listaFiltrada, filtro).map((ag) => <Cartao key={ag.id} ag={ag} destaque={ag.id === destaqueId} />)}
      </ul>
    );
  }

  // --- Estados de página ---
  if (estado === 'carregando') {
    return (
      <div className="agendamentos-cliente-container">
        <EstadoCarregando mensagem="Carregando agendamentos…" linhas={3} />
      </div>
    );
  }

  if (estado === 'naoEncontrado') {
    return (
      <div className="agendamentos-cliente-container">
        <EstadoErro
          fullPage
          mensagem="Cliente não encontrado"
          detalhe="Volte para a lista de clientes e tente de novo."
          onRetry={() => onNavegar('clientes')}
          rotuloRetry="Ir para clientes"
          iconeRetry="fa-solid fa-arrow-left"
        />
      </div>
    );
  }

  if (estado === 'erro') {
    return (
      <div className="agendamentos-cliente-container">
        <EstadoErro
          fullPage
          mensagem="Não foi possível carregar os agendamentos"
          detalhe="Verifique sua conexão e tente novamente."
          onRetry={carregar}
          rotuloRetry="Tentar de novo"
        />
      </div>
    );
  }

  const listaTotalVazia = agendamentos.length === 0;

  return (
    <div className="agendamentos-cliente-container">
      <button type="button" className="ac-voltar" onClick={voltarParaCliente}>
        ← Voltar para o cliente
      </button>

      <header className="ac-cabecalho">
        <div className="ac-cabecalho__texto">
          <h1>
            Agendamentos <span className="ac-cabecalho__cliente">— {cliente.nome}</span>
          </h1>
          {cliente.documentoFormatado && <p className="ac-cabecalho__doc">{cliente.documentoFormatado}</p>}
        </div>
        {!listaTotalVazia && (
          <button type="button" className="btn-primary ac-cta-novo" onClick={abrirModal}>
            + Novo agendamento
          </button>
        )}
      </header>

      {listaTotalVazia ? (
        <EstadoVazio
          icone="fa-solid fa-calendar-days"
          titulo="Nenhum agendamento ainda"
          descricao="Quando você agendar reuniões, ligações ou outras ações para este cliente, elas aparecem aqui."
          acao={{ rotulo: '+ Criar primeiro agendamento', onClick: abrirModal }}
        />
      ) : (
        <>
          <div className="ac-filtros" role="group" aria-label="Filtrar por status">
            {FILTROS.map((f) => {
              const ativo = filtro === f.chave;
              const total = contagens[f.chave] != null ? contagens[f.chave] : 0;
              return (
                <button
                  key={f.chave}
                  type="button"
                  aria-pressed={ativo}
                  className={`ac-pill${ativo ? ' ac-pill--ativo' : ''}`}
                  onClick={() => setFiltro(f.chave)}
                >
                  {f.rotulo} · {total}
                </button>
              );
            })}
          </div>

          <div className="ac-conteudo">{renderLista()}</div>
        </>
      )}

      {/* Toasts */}
      <div className="ac-toasts" aria-live="polite">
        {toasts.map((t) => (
          <div key={t.id} className={`ac-toast ac-toast--${t.tipo}`} role={t.tipo === 'erro' ? 'alert' : 'status'}>
            {t.texto}
          </div>
        ))}
      </div>

      {/* Modal: novo agendamento */}
      {modalAberto && (
        <div className="ac-modal-overlay" onClick={fecharModal}>
          <div
            className="ac-modal"
            role="dialog"
            aria-modal="true"
            aria-labelledby="ac-modal-titulo"
            ref={modalRef}
            onClick={(e) => e.stopPropagation()}
            onKeyDown={(e) => aoTeclarDialog(e, modalRef, fecharModal)}
          >
            <div className="ac-modal__cabecalho">
              <h2 id="ac-modal-titulo">Novo agendamento</h2>
              <button type="button" className="ac-modal__fechar" aria-label="Fechar" onClick={fecharModal}>×</button>
            </div>
            <form className="ac-modal__corpo" onSubmit={salvarAgendamento} noValidate>
              <div className="form-agendamento">
                <div className="form-grupo">
                  <label htmlFor="ac-data">Data</label>
                  <input
                    id="ac-data"
                    name="data"
                    type="date"
                    value={form.data}
                    onChange={aoMudarCampo}
                    ref={primeiroCampoRef}
                    aria-invalid={errosForm.data ? 'true' : undefined}
                    aria-describedby={errosForm.data ? 'ac-data-erro' : undefined}
                    className={errosForm.data ? 'ac-campo--invalido' : undefined}
                  />
                  {dataNoPassado && <span className="ac-aviso">Esta data já passou.</span>}
                  {errosForm.data && <span id="ac-data-erro" className="ac-erro-campo">{errosForm.data}</span>}
                </div>
                <div className="form-grupo">
                  <label htmlFor="ac-hora">Hora</label>
                  <input
                    id="ac-hora"
                    name="hora"
                    type="time"
                    value={form.hora}
                    onChange={aoMudarCampo}
                    aria-invalid={errosForm.hora ? 'true' : undefined}
                    aria-describedby={errosForm.hora ? 'ac-hora-erro' : undefined}
                    className={errosForm.hora ? 'ac-campo--invalido' : undefined}
                  />
                  {errosForm.hora && <span id="ac-hora-erro" className="ac-erro-campo">{errosForm.hora}</span>}
                </div>
                <div className="form-grupo">
                  <label htmlFor="ac-tipo">Tipo de ação</label>
                  <select
                    id="ac-tipo"
                    name="tipo"
                    value={form.tipo}
                    onChange={aoMudarCampo}
                    aria-invalid={errosForm.tipo ? 'true' : undefined}
                    aria-describedby={errosForm.tipo ? 'ac-tipo-erro' : undefined}
                    className={errosForm.tipo ? 'ac-campo--invalido' : undefined}
                  >
                    <option value="reuniao">Reunião</option>
                    <option value="ligacao">Ligação</option>
                    <option value="email">E-mail</option>
                    <option value="visita">Visita</option>
                    <option value="outro">Outro</option>
                  </select>
                  {errosForm.tipo && <span id="ac-tipo-erro" className="ac-erro-campo">{errosForm.tipo}</span>}
                </div>
                <div className="form-grupo">
                  <label htmlFor="ac-responsavel">Responsável</label>
                  <input
                    id="ac-responsavel"
                    type="text"
                    value="Você (usuário logado)"
                    readOnly
                    aria-describedby="ac-responsavel-hint"
                  />
                  <span id="ac-responsavel-hint" className="ac-campo-hint">
                    Por enquanto o responsável é o usuário logado; escolher outra pessoa virá em breve.
                  </span>
                </div>
                <div className="form-grupo form-grupo--full">
                  <label htmlFor="ac-assunto">Assunto</label>
                  <input
                    id="ac-assunto"
                    name="assunto"
                    type="text"
                    maxLength={120}
                    placeholder="Ex.: Renovação da marca, envio de procuração…"
                    value={form.assunto}
                    onChange={aoMudarCampo}
                    aria-invalid={errosForm.assunto ? 'true' : undefined}
                    aria-describedby={errosForm.assunto ? 'ac-assunto-erro' : undefined}
                    className={errosForm.assunto ? 'ac-campo--invalido' : undefined}
                  />
                  {errosForm.assunto && <span id="ac-assunto-erro" className="ac-erro-campo">{errosForm.assunto}</span>}
                </div>
                <div className="form-grupo form-grupo--full">
                  <label htmlFor="ac-descricao">Descrição</label>
                  <textarea
                    id="ac-descricao"
                    name="descricao"
                    rows="4"
                    placeholder="Detalhes, links, observações (opcional)"
                    value={form.descricao}
                    onChange={aoMudarCampo}
                  />
                </div>
              </div>
              {erroSalvar && <p className="ac-erro-salvar" role="alert">Não foi possível salvar. Tente novamente.</p>}
              <div className="ac-modal__rodape">
                <button type="button" className="btn-secondary" onClick={fecharModal} disabled={salvando}>Cancelar</button>
                <button type="submit" className="btn-primary" disabled={salvando}>
                  {salvando ? 'Salvando…' : 'Salvar agendamento'}
                </button>
              </div>
            </form>
          </div>
        </div>
      )}

      {/* Diálogo: confirmar cancelamento */}
      {confirmacao && (
        <div className="ac-modal-overlay" onClick={() => setConfirmacao(null)}>
          <div
            className="ac-modal ac-modal--confirma"
            role="alertdialog"
            aria-modal="true"
            aria-labelledby="ac-confirma-titulo"
            aria-describedby="ac-confirma-desc"
            ref={confirmRef}
            onClick={(e) => e.stopPropagation()}
            onKeyDown={(e) => aoTeclarDialog(e, confirmRef, () => setConfirmacao(null))}
          >
            <div className="ac-modal__cabecalho">
              <h2 id="ac-confirma-titulo">Cancelar este agendamento?</h2>
            </div>
            <div className="ac-modal__corpo">
              <p id="ac-confirma-desc">Ele continuará visível na lista, marcado como cancelado.</p>
            </div>
            <div className="ac-modal__rodape">
              {/* eslint-disable-next-line jsx-a11y/no-autofocus */}
              <button type="button" className="btn-secondary" onClick={() => setConfirmacao(null)} autoFocus>Voltar</button>
              <button type="button" className="ac-btn-perigo" onClick={confirmarCancelamento}>Sim, cancelar</button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

export default AgendamentosCliente;
